import React, { FC, MutableRefObject, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import CLanguageHandler from '../../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState } from '../../../../../React/Common/Contexts'
import { PaginationInfo } from '../../../../../NonReact/Types/Types'
import {
    CRMConfigurationEntry,
    CRMConfigurations,
    CRMConfigurationsResponse
} from '../../../../../NonReact/Types/CRMConfiguration/Types'
import {
    AdminFilters,
    AdminFilterSingleValues,
    AdminFilterValues,
    AdminFiltersSlotRenderArgs
} from '../../../Molecules/AdminFilters/AdminFilters'
import { SelectFilter, SelectFilterOption } from '../../../Molecules/AdminFilters/SelectFilter'
import { CrmIcon } from '../../../Atoms/Icons/Crm'
import { displayError } from '../../../../../React/Common/Utils/Messages'
import { PageTitle } from '../../../Molecules/PageTitle/PageTitle'
import { AdminBackendPermissionNames } from '../../../../../React/Common/Constants/Permissions'
import { DataTable } from '../../../Molecules/DataTable/DataTable'
import {
    CRMConfigurationLanguage,
    GenericAdminFilterLanguage,
    GenericAdminListLanguage,
    Router as RouterLang
} from '../../../../../../i18n'
import CRMModal from '../Modal/Modal/CRMModal'
import { DEFAULT_ROWS_PER_PAGE, UseAdminStatState, useAdminTableState } from '../../../../Hooks/useAdminStatState'
import {
    ALL_VALUE,
    ANY_VALUE_STRING_DEFAULT,
    ANY_VALUE_STRING_NUMERIC
} from '../../../../../React/Common/Constants/DefaultValues'
import { getCRMConfigurationsList } from '../../../../../React/Common/API/ItemsGetters'
import { SelectedCRMConfigEntry } from '../Modal/Types/Types'
import { EMPTY_STRING } from '../../../../../React/Common/Constants/Separators'
import { DEFAULT_CME_ID } from '../../../../../React/Common/Constants/ServerValues'
import { deleteCRMConfigurations, getCRMTypes } from '../../../../../NonReact/common/API/EntityAPIClient'
import { UserInfo, useUserInfo } from '../../../../Hooks/useUserInfo'
import { CRMTypes, useCRMTypes } from '../../../../Hooks/useCRMTypes'
import { IsVarEmpty, isVarNotEmpty } from '../../../../../NonReact/utils/GlobalFunctions'
import { hasResolvedInitialTelSysPartitionListFilters } from '../../../../Utils/adminListFilterReadiness'
import { CRMTypesResponse, JSONResponse } from '../../../../../NonReact/common/Types/Types'
import { RxJS } from '../../../../../NonReact/common/RxJS/RxJS'
import { CMEFields } from '../../../../../NonReact/constants/ApiConstants'
import { CME } from '../../../../../React/Common/Types/Types'
import { isBitmaskMatching } from '../../../../../React/Common/Utils/BitwiseOperations'
import { CapabilityBitFlags } from '../../../../../React/CMEAddModify/Constants'

export const CRMIntegrationList: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(CRMConfigurationLanguage.NAME)
    language.loadBundle(GenericAdminListLanguage.NAME)
    language.loadBundle(GenericAdminFilterLanguage.NAME)

    const [crmConfigurationList, setCRMConfigurationList]: ReactState<Nullable<CRMConfigurations>> =
        useState<Nullable<CRMConfigurations>>()

    const {
        loading,
        setLoading,
        sort,
        setSort,
        filters,
        setFilters,
        pagination,
        setPagination,
        totalCount,
        setTotalCount
    }: UseAdminStatState = useAdminTableState()

    const [selectedCRMConfig, setSelectedCRMConfig]: ReactState<Definable<SelectedCRMConfigEntry>> =
        useState<Definable<SelectedCRMConfigEntry>>()
    const [selectedFilters, setSelectedFilters]: ReactState<Nullable<AdminFilterSingleValues>> =
        useState<Nullable<AdminFilterSingleValues>>(null)
    const openState: ReactState<boolean> = useState<boolean>(false)
    const [isViewOnly, setIsViewOnly]: ReactState<boolean> = useState<boolean>(false)
    const [isOpen, setIsOpen]: ReactState<boolean> = openState

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [userInfo]: UserInfo = useUserInfo()

    const [crmTypeOpts]: CRMTypes = useCRMTypes()

    const [crmTypeFilter, setCrmTypeFilter]: ReactState<Nullable<SelectFilterOption>> = useState<
        Nullable<SelectFilterOption>
    >({
        value: ANY_VALUE_STRING_NUMERIC,
        label: ANY_VALUE_STRING_DEFAULT
    })
    const [hasReceivedInitialFilters, setHasReceivedInitialFilters]: ReactState<boolean> = useState<boolean>(false)

    const canRequestCrmList: boolean = useMemo(
        (): boolean => isVarNotEmpty(userInfo?.Scope?.TelSysID) && isVarNotEmpty(userInfo?.Scope?.PartIDInTelSys),
        [userInfo]
    )

    const mappedCrmTypeOpts: SelectFilterOption[] = useMemo(
        (): SelectFilterOption[] => [
            { value: ANY_VALUE_STRING_NUMERIC, label: ANY_VALUE_STRING_DEFAULT },
            ...(crmTypeOpts?.map(
                ({ crmType, crmTypeName }: CRMTypesResponse): SelectFilterOption => ({
                    label: crmTypeName,
                    value: crmType
                })
            ) ?? [])
        ],
        [crmTypeOpts]
    )

    const getCRMConfiguration = useCallback((): void => {
        if (IsVarEmpty(userInfo?.Scope?.TelSysID) || IsVarEmpty(userInfo?.Scope?.PartIDInTelSys)) return

        if (!hasReceivedInitialFilters) return

        setLoading((currentLoading: number): number => currentLoading + 1)

        const [currentPage, rowsPerPage]: PaginationInfo = pagination

        if (isVarNotEmpty(filters) && filters?.noTelephonyServer) {
            setCRMConfigurationList([])
            setTotalCount(0)
            setLoading((currentLoading: number): number => currentLoading - 1)
            return
        }

        getCRMConfigurationsList(
            filters?.telephonyServer?.value ?? userInfo?.Scope?.TelSysID[0] ?? ALL_VALUE,
            filters?.partition?.value ?? userInfo?.Scope?.PartIDInTelSys[0] ?? ALL_VALUE,
            currentPage * rowsPerPage,
            rowsPerPage,
            sort,
            filters?.search,
            crmTypeFilter?.value?.toString() ?? EMPTY_STRING
        )
            .then((CRMConfigurationListLocal: Nullable<CRMConfigurationsResponse>): void => {
                if (isMounted.current) {
                    setCRMConfigurationList(
                        CRMConfigurationListLocal?.Data?.Items?.map(
                            (crmConfig: CRMConfigurationEntry): CRMConfigurationEntry => ({
                                ...crmConfig,
                                key: crmConfig.CRMConfigurationID
                            })
                        )
                    )
                    setTotalCount(CRMConfigurationListLocal?.Data?.Total ?? 0)
                }
            })
            .catch(displayError)
            .finally((): void => {
                if (isMounted.current) setLoading((currentLoading: number): number => currentLoading - 1)
            })
    }, [
        filters,
        crmTypeFilter?.value,
        hasReceivedInitialFilters,
        sort,
        pagination,
        JSON.stringify(userInfo?.Scope?.TelSysID),
        JSON.stringify(userInfo?.Scope?.PartIDInTelSys)
    ])

    useEffect((): void => {
        if (IsVarEmpty(userInfo)) return

        getCRMTypes(userInfo?.Scope?.CMEnvironmentID, userInfo?.Scope?.TelSysID?.toString())
            .then((response: JSONResponse<CRMTypesResponse[]>): void => {
                RxJS.crmTypes?.announce(response ?? [])
            })
            .catch((): void => {
                RxJS.crmTypes?.announce([])
            })
    }, [JSON.stringify(userInfo)])

    useEffect(getCRMConfiguration, [
        filters,
        crmTypeFilter?.value,
        hasReceivedInitialFilters,
        sort,
        pagination,
        JSON.stringify(userInfo?.Scope?.TelSysID),
        JSON.stringify(userInfo?.Scope?.PartIDInTelSys)
    ])

    useEffect((): void => {
        if (IsVarEmpty(crmConfigurationList)) return

        setCRMConfigurationList(
            (prev: Nullable<CRMConfigurations>): CRMConfigurations =>
                prev?.map(
                    (item: CRMConfigurationEntry): CRMConfigurationEntry => ({
                        ...item,
                        CRMType:
                            crmTypeOpts?.find((crmType: CRMTypesResponse): boolean => crmType.crmType === item.CRMType)
                                ?.crmTypeName ?? item.CRMType
                    })
                ) ?? []
        )
    }, [JSON.stringify(crmConfigurationList), crmTypeOpts])

    return (
        <div className='crm-configuration-list admin-list-container'>
            <PageTitle title={RouterLang.PageTitle_crmIntegration}></PageTitle>
            <div className='crmcl-table-container admin-table-list-container'>
                <AdminFilters
                    localeText={{
                        cmeLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_CME_LABEL),
                        telephonyServerLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_TELSYS_LABEL
                        ),
                        partitionLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_PARTITION_LABEL
                        ),
                        includeChildCmeLabel: language.get(
                            GenericAdminFilterLanguage.GenericAdminFilterLanguage_INCLUDE_CHILD_CMES_LABEL
                        ),
                        searchLabel: language.get(GenericAdminFilterLanguage.GenericAdminFilterLanguage_SEARCH_LABEL)
                    }}
                    onChange={(values: AdminFilterValues): void => {
                        setFilters(values)

                        if (hasResolvedInitialTelSysPartitionListFilters(values)) {
                            setHasReceivedInitialFilters(true)
                        }
                    }}
                    optionsToDisplay={{
                        cme: true,
                        telephonyServer: true,
                        partition: true,
                        crmType: true,
                        scope: false,
                        codeType: false,
                        userType: false,
                        includeCmeChildren: true,
                        search: true,
                        includeRefresh: true
                    }}
                    additionalFilterSkeletonCount={1}
                    additionalFilters={({ isLoading }: AdminFiltersSlotRenderArgs): ReactElement => ( // NOSONAR
                        <div>
                            <SelectFilter
                                onChange={(value: Nullable<SelectFilterOption>): void => {
                                    setCrmTypeFilter(value)
                                    setPagination(([, rowsPerPage]: PaginationInfo): PaginationInfo => [0, rowsPerPage])
                                }}
                                options={mappedCrmTypeOpts}
                                label={language.get(
                                    GenericAdminFilterLanguage.GenericAdminFilterLanguage_CRM_TYPE_LABEL
                                )}
                                icon={<CrmIcon />}
                                disabled={isLoading}
                                value={crmTypeFilter}
                                defaultValue={ANY_VALUE_STRING_NUMERIC}
                            />
                        </div>
                    )}
                    onRefresh={getCRMConfiguration}
                    cmeFiltering={[
                        [CMEFields.CAPABILITIES],
                        (cme: CME): boolean =>
                            isBitmaskMatching(cme.Capabilities ?? 0, CapabilityBitFlags.CXI_BROADWORKS) ||
                            isBitmaskMatching(cme.Capabilities ?? 0, CapabilityBitFlags.CXI_WEBEX) ||
                            isBitmaskMatching(cme.Capabilities ?? 0, CapabilityBitFlags.CXI_TEAMS)
                    ]}
                />
                <DataTable
                    permissionName={AdminBackendPermissionNames.CRM_CONFIG}
                    localeText={{
                        addButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Add),
                        viewButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_View),
                        changeButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Change),
                        deleteButton: language.get(GenericAdminListLanguage.GenericAdminList_Footer_Delete),
                        selectionActive: (count: number): string =>
                            language.getInterpolated(GenericAdminListLanguage.GenericAdminList_Footer_Selected, count),
                        noRows: language.get(GenericAdminListLanguage.GenericAdminList_Footer_NoRows)
                    }}
                    defaultRowsPerPageOptions={[10, 25, 50, 100]}
                    defaultRowsPerPage={DEFAULT_ROWS_PER_PAGE}
                    totalCount={totalCount}
                    colIndexToTriggerClick={0}
                    isSelectable
                    isLoading={loading > 0 || (canRequestCrmList && !hasReceivedInitialFilters)}
                    paginationServer
                    sortServer
                    onToolbarViewClick={([selectedItem]: CRMConfigurations): void => {
                        setSelectedCRMConfig({
                            telsysID: selectedItem.TelSysID,
                            partitionID: selectedItem.PartIDInTelSys,
                            deviceID: selectedItem.DeviceNumber,
                            id: selectedItem.CRMConfigurationID
                        })
                        setIsViewOnly(true)
                        setIsOpen(true)
                    }}
                    onToolbarChangeClick={([selectedItem]: CRMConfigurations): void => {
                        setSelectedCRMConfig({
                            telsysID: selectedItem.TelSysID,
                            partitionID: selectedItem.PartIDInTelSys,
                            deviceID: selectedItem.DeviceNumber,
                            id: selectedItem.CRMConfigurationID
                        })
                        setIsViewOnly(false)
                        setIsOpen(true)
                    }}
                    onToolbarAddClick={(): void => {
                        setSelectedCRMConfig(undefined)
                        setSelectedFilters({
                            cme: filters?.cme?.value ?? EMPTY_STRING,
                            telephonyServer: filters?.telephonyServer?.value ?? ALL_VALUE,
                            partition: filters?.partition?.value ?? ALL_VALUE,
                            device: EMPTY_STRING
                        })
                        setIsViewOnly(false)
                        setIsOpen(true)
                    }}
                    onToolbarDeleteClick={(selectedItems: CRMConfigurations): void => {
                        setLoading((currentLoading: number): number => currentLoading + 1)
                        deleteCRMConfigurations(selectedItems)
                            .then(getCRMConfiguration)
                            .catch((e: Error): void => displayError(e))
                            .finally((): void => setLoading((currentLoading: number): number => currentLoading - 1))
                    }}
                    onSortChange={setSort}
                    onRowsPerPageChange={(currentPage: number, newRowsPerPage: number): void =>
                        setPagination([currentPage, newRowsPerPage])
                    }
                    onPageChange={(newPage: number, rowsPerPage: number): void => setPagination([newPage, rowsPerPage])}
                    columns={[
                        {
                            headerName: language.get(
                                CRMConfigurationLanguage.CRMConfiguration_Table_Headers_Description
                            ),
                            field: 'Description',
                            key: 'crmcltv-description',
                            sortable: true
                        },
                        {
                            headerName: language.get(CRMConfigurationLanguage.CRMConfiguration_Table_Headers_Type),
                            field: 'CRMType',
                            key: 'crmltv-type',
                            sortable: true
                        },
                        {
                            headerName: language.get(CRMConfigurationLanguage.CRMConfiguration_Table_Headers_TelSys),
                            field: 'TelSysDescription',
                            key: 'crmltv-telsys',
                            sortable: true
                        },
                        {
                            headerName: language.get(CRMConfigurationLanguage.CRMConfiguration_Table_Headers_Partition),
                            field: 'PartitionDescription',
                            key: 'crmltv-partition',
                            sortable: true
                        },
                        {
                            headerName: language.get(CRMConfigurationLanguage.CRMConfiguration_Table_Headers_Device),
                            field: 'DeviceDescription',
                            key: 'crmltv-device',
                            sortable: true
                        }
                    ]}
                    data={crmConfigurationList ?? []}
                />
                {isOpen ? (
                    <CRMModal
                        openState={openState}
                        selectedCRMConfig={selectedCRMConfig}
                        isViewOnly={isViewOnly}
                        filters={selectedFilters}
                        closeModal={(refresh?: boolean): void => {
                            setIsOpen(false)

                            if (refresh) {
                                getCRMConfiguration()
                            }
                        }}
                    />
                ) : (
                    <></>
                )}
            </div>
        </div>
    )
}
